/**
 * Root cause — is a machine × component problem the operator's, the
 * equipment's, or both?
 *
 * Two independent signals, each scored 0–1:
 *
 * - **Operator signal** — does one operator carry far more of the
 *   component's operator-sensitive events than their share of the machine's
 *   hours? `excess = topOperator.eventShare − topOperator.hoursShare`;
 *   `operatorSignal = clamp(excess / 0.4)` once the component has at least
 *   three events and the top operator carries at least three of them (fewer
 *   is noise). A proportional spread scores 0; one operator carrying 80 % of
 *   events on 50 % of hours scores 0.75. **Chance check:** the one-sided
 *   binomial tail P(X ≥ top events | n, hoursShare), Bonferroni-adjusted over
 *   the operators with events, must be ≤ 0.02; otherwise the signal is capped
 *   at 0.4 × itself so it can never call a cause. Without this, a 7/7-day
 *   roster block puts a clump of events on whoever drove that week by chance.
 * - **Equipment signal** — is the component itself going? Condition score
 *   < 20 → 1.0, < 40 → 0.9, < 60 → 0.65, < 80 → 0.25, else 0; plus 0.25 when
 *   the machine's tracked metric belongs to this component and drifts more
 *   than 0.4 %/day (the `predict_risk` drift rule). Capped at 1.
 *
 * | operator ≥ 0.5 | equipment ≥ 0.5 | cause |
 * |---|---|---|
 * | yes | yes | `mixed` |
 * | yes | no | `operator` |
 * | no | yes | `equipment` |
 * | no | no | `insufficient` |
 *
 * Confidence is `0.5 + 0.45 × strength × weight`, where strength is the
 * deciding signal (the weaker of the two for `mixed`) and weight is how much
 * data stands behind it — `min(1, events / 10)` for an operator call,
 * `0.7 + 0.3 × min(1, events / 10)` for an equipment call (the condition score
 * is evidence even with no alarms). `insufficient` reports
 * `0.2 + 0.3 × max(signals)` — how close it came.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { BY_ID, HEALTH } from '../../data/core.ts';
import type { ConcentratedOperator, RootCause, RootCauseFinding } from '../../schema/operations.ts';
import type { DailyMetricName, DailyMetricRow, RmsHealthRegistry } from '../../schema/telemetry.ts';
import type { WorkOrder } from '../../schema/work-order.ts';
import { slope } from '../oem-engine.ts';
import { CONDITION_BAND_LABEL, bandOf } from '../../schema/machine.ts';
import { ABUSE_CODE } from '../seed/alarm-codes.ts';
import { metricInfoFor } from '../seed/metric-info.ts';
import { OPERATOR_BY_ID } from '../seed/operators.ts';
import { attribute, machineOperatorShares, type Attribution } from './attribution.ts';
import { round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';

/** What the classifier reads. `workOrders` and `rmsHealth` only add evidence lines. */
export interface RootCauseCtx {
  readonly attribution: Attribution;
  readonly dailyMetric: readonly DailyMetricRow[];
  readonly workOrders?: readonly WorkOrder[];
  readonly rmsHealth?: RmsHealthRegistry;
}

/** Signal threshold for calling a cause. */
export const CAUSE_THRESHOLD = 0.5;

/** Fewer operator-sensitive events than this and the operator signal is 0. */
export const MIN_EVENTS = 3;

/** Adjusted one-sided p-value at or below which a concentration counts as real. */
export const SIGNIFICANCE = 0.02;

/** Multiplier on the operator signal when the concentration is not significant. */
export const NOT_SIGNIFICANT_CAP = 0.4;

/**
 * P(X ≥ k) for X ~ Binomial(n, p) — the chance an operator with hours share
 * `p` carries at least `k` of `n` events if events simply followed hours.
 */
export function binomialTail(k: number, n: number, p: number): number {
  if (k <= 0) return 1;
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  let sum = 0;
  let logC = 0; // log C(n, i), built up from i = 0
  for (let i = 0; i <= n; i += 1) {
    if (i > 0) logC += Math.log((n - i + 1) / i);
    if (i >= k) sum += Math.exp(logC + i * Math.log(p) + (n - i) * Math.log(1 - p));
  }
  return Math.min(1, sum);
}

/** Which component each tracked metric is about (regex source, case-insensitive). */
export const METRIC_COMPONENT: Readonly<Record<DailyMetricName, string>> = {
  rear_brake_temp_c: 'retarder|brake',
  hyd_oil_temp_c: 'hydraulic pump',
  percussion_pressure_bar: 'rock drill',
  penetration_rate_m_h: 'dth|rock drill',
};

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
const pct = (n: number): string => `${Math.round(n * 100)}%`;

/** The component's condition score → equipment signal before drift. */
export function conditionSignal(score: number): number {
  if (score < 20) return 1;
  if (score < 40) return 0.9;
  if (score < 60) return 0.65;
  if (score < 80) return 0.25;
  return 0;
}

/** Daily-metric drift for a machine, %/day, and the metric it is on. */
export function driftOf(
  machine: string,
  dailyMetric: readonly DailyMetricRow[],
): { metric: DailyMetricName | null; pctPerDay: number; first: number | null; last: number | null } {
  const rows = dailyMetric.filter((d) => d.machine_id === machine);
  const vals = rows.map((d) => d.value);
  return {
    metric: rows[0]?.metric ?? null,
    pctPerDay: (slope(vals) / Math.max(1, Math.abs(vals[0] ?? 0))) * 100,
    first: vals[0] ?? null,
    last: vals[vals.length - 1] ?? null,
  };
}

/** Classify one machine × component. */
export function rootCause(machine: string, component: string, ctx: RootCauseCtx): RootCauseFinding {
  const m = BY_ID[machine];
  const evidence: string[] = [];

  // ── Operator signal ──────────────────────────────────────────────────────
  const shares = machineOperatorShares(ctx.attribution, machine, { component });
  const n = shares.reduce((s, o) => s + o.events, 0);
  const withEvents = shares.filter((o) => o.events > 0);
  const top = withEvents
    .slice()
    .sort((a, b) => b.events - a.events || b.eventShare / (b.hoursShare || 1) - a.eventShare / (a.hoursShare || 1))[0];
  const concentrationIndex = withEvents.length
    ? Math.max(...withEvents.map((o) => (o.hoursShare > 0 ? o.eventShare / o.hoursShare : o.eventShare * 10)))
    : 1;
  const excess = top ? top.eventShare - top.hoursShare : 0;
  // Chance check: how likely is the top operator to carry this many events if
  // events simply followed hours? One-sided binomial tail, Bonferroni-adjusted
  // for the number of operators who could have been "top".
  const pValue = top && n > 0 ? Math.min(1, binomialTail(top.events, n, top.hoursShare) * withEvents.length) : null;
  const significant = pValue !== null && pValue <= SIGNIFICANCE;
  // One or two events on one operator is noise, however few hours they drove;
  // a pattern that chance explains is capped below the calling threshold.
  const operatorSignal =
    n >= MIN_EVENTS && top && top.events >= MIN_EVENTS
      ? clamp01(excess / 0.4) * (significant ? 1 : NOT_SIGNIFICANT_CAP)
      : 0;

  let topOperator: ConcentratedOperator | null = null;
  if (top) {
    const shiftHours = { D: 0, N: 0 };
    for (const a of ctx.attribution.assignments) {
      if (a.machine === machine && a.operatorId === top.operatorId) shiftHours[a.shift] += a.hours;
    }
    const op = OPERATOR_BY_ID[top.operatorId];
    topOperator = {
      operatorId: top.operatorId,
      name: top.name,
      crew: top.crew,
      shift: shiftHours.N > shiftHours.D ? 'N' : 'D',
      events: top.events,
      eventShare: round(top.eventShare, 3),
      hoursShare: round(top.hoursShare, 3),
      synthesised: op?.synthesised ?? true,
    };
  }

  // ── Equipment signal ─────────────────────────────────────────────────────
  const reg = HEALTH.find((h) => h.machine_id === machine && h.component === component);
  const drift = driftOf(machine, ctx.dailyMetric);
  const driftOnComponent =
    drift.metric !== null && new RegExp(METRIC_COMPONENT[drift.metric], 'i').test(component);
  const drifting = driftOnComponent && Math.abs(drift.pctPerDay) > 0.4;
  const equipmentSignal = clamp01((reg ? conditionSignal(reg.health_score) : 0) + (drifting ? 0.25 : 0));

  // ── Evidence, strongest first ────────────────────────────────────────────
  const opLine = (() => {
    if (!topOperator || n === 0) return null;
    const shiftWord = topOperator.shift === 'N' ? 'Night' : 'Day';
    const what = topOperator.events === n && n === 1 ? 'the one event' : `${topOperator.events} of ${n} events`;
    const chance =
      pValue === null || n < MIN_EVENTS
        ? ''
        : significant
          ? ` — not chance (p ${pValue < 0.001 ? '< 0.001' : `= ${pValue.toFixed(3)}`})`
          : ` — within chance (p = ${pValue.toFixed(2)})`;
    return `${shiftWord}-shift operator ${topOperator.name} (crew ${topOperator.crew}) logged ${what} (${pct(topOperator.eventShare)}) on ${pct(topOperator.hoursShare)} of ${machine}'s hours${chance}`;
  })();
  const condLine = reg
    ? `${component} condition ${reg.health_score}/100 (${CONDITION_BAND_LABEL[bandOf(reg.health_score).key].toLowerCase()})${reg.note ? ` — "${reg.note}"` : ''}`
    : null;
  const driftLine =
    driftOnComponent && drift.metric && drift.first !== null && drift.last !== null
      ? `${metricInfoFor(drift.metric).name} ${drift.pctPerDay > 0 ? '+' : ''}${drift.pctPerDay.toFixed(1)}%/day (${drift.first} → ${drift.last} ${metricInfoFor(drift.metric).unit})${drifting ? '' : ' — within band'}`
      : null;
  const spreadLine =
    n >= MIN_EVENTS && operatorSignal < CAUSE_THRESHOLD
      ? `Events spread across ${withEvents.length} operator${withEvents.length === 1 ? '' : 's'} in line with their hours (top share ${pct(top?.eventShare ?? 0)} on ${pct(top?.hoursShare ?? 0)} of hours)`
      : null;

  if (operatorSignal >= equipmentSignal) {
    if (opLine && operatorSignal > 0) evidence.push(opLine);
    if (condLine) evidence.push(condLine);
  } else {
    if (condLine) evidence.push(condLine);
    if (opLine && operatorSignal > 0) evidence.push(opLine);
  }
  if (driftLine) evidence.push(driftLine);
  if (spreadLine) evidence.push(spreadLine);

  if (ctx.rmsHealth && operatorSignal > 0) {
    const b = ctx.rmsHealth[machine]?.operator_behaviour_7d;
    if (b) {
      const parts = Object.entries(b)
        .filter(([, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${v} ${k.replace(/_events$|_selections$/, '').replace(/_/g, ' ')}`);
      if (parts.length) evidence.push(`RMS 7-day operator behaviour on ${machine}: ${parts.join(', ')}`);
    }
  }
  if (ctx.workOrders) {
    const wos = ctx.workOrders.filter(
      (w) => w.machine_id === machine && w.component === component && w.wo_type !== 'Preventive',
    );
    const opCaused = wos.filter((w) => w.failure_cause === 'Operator abuse' || w.failure_cause === 'Operational');
    if (wos.length) {
      evidence.push(
        `Work-order history: ${wos.length} corrective/breakdown order${wos.length === 1 ? '' : 's'} on this component${opCaused.length ? `, ${opCaused.length} coded operational or operator abuse` : ''}`,
      );
    }
  }

  // ── Classification ───────────────────────────────────────────────────────
  const op = operatorSignal >= CAUSE_THRESHOLD;
  const eq = equipmentSignal >= CAUSE_THRESHOLD;
  const cause: RootCause = op && eq ? 'mixed' : op ? 'operator' : eq ? 'equipment' : 'insufficient';
  const opWeight = Math.min(1, n / 10);
  const eqWeight = 0.7 + 0.3 * Math.min(1, n / 10);
  const confidence =
    cause === 'mixed'
      ? 0.5 + 0.45 * Math.min(operatorSignal * opWeight, equipmentSignal * eqWeight)
      : cause === 'operator'
        ? 0.5 + 0.45 * operatorSignal * opWeight
        : cause === 'equipment'
          ? 0.5 + 0.45 * equipmentSignal * eqWeight
          : 0.2 + 0.3 * Math.max(operatorSignal, equipmentSignal);

  return {
    machine,
    mine: m.mine,
    component,
    cause,
    confidence: round(confidence, 2),
    operatorSignal: round(operatorSignal, 2),
    equipmentSignal: round(equipmentSignal, 2),
    events: n,
    concentrationIndex: round(concentrationIndex, 2),
    pValue: pValue === null ? null : round(pValue, 4),
    topOperator,
    evidence,
  };
}

/**
 * Every machine × component worth classifying: any component with at least
 * one operator-sensitive event (abuse included), plus any component in poor
 * condition or worse (< 60). Sorted by cause (mixed, operator, equipment,
 * insufficient), then confidence.
 */
export function rootCauses(ctx: RootCauseCtx): RootCauseFinding[] {
  const keys = new Set<string>();
  for (const e of ctx.attribution.events) {
    if (e.component && e.sensitivity !== 'none') keys.add(`${e.machine}|${e.component}`);
  }
  for (const h of HEALTH) if (h.health_score < 60) keys.add(`${h.machine_id}|${h.component}`);
  const order: Record<RootCause, number> = { mixed: 0, operator: 1, equipment: 2, insufficient: 3 };
  return [...keys]
    .map((k) => {
      const [machine, component] = k.split('|');
      return rootCause(machine, component, ctx);
    })
    .sort((a, b) => order[a.cause] - order[b.cause] || b.confidence - a.confidence || a.machine.localeCompare(b.machine));
}

/** Fetch, attribute and classify everything. */
export async function loadRootCauses(): Promise<RootCauseFinding[]> {
  const inputs = await loadReliabilityInputs();
  return rootCauses({ ...inputs, attribution: attribute(inputs) });
}

/** The pseudo-code abuse is attributed under, re-exported for consumers of findings. */
export { ABUSE_CODE };
